import { useMutation, useQuery, useQueryClient, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { vendorBillsApi } from './api';
import type {
  CreateVendorBillPayload, PurchaseDocumentPayload, RecordVendorPaymentPayload, UpdateVendorBillPayload,
  VendorBill, VendorBillListParams, VendorBillListResponse, VendorBillStats,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useVendorBills(params: VendorBillListParams): UseQueryResult<VendorBillListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['vendor-bills', tenantId, 'list', params],
    queryFn: () => vendorBillsApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useVendorBillStats(): UseQueryResult<VendorBillStats> {
  const tenantId = useTenantId();
  return useQuery({ queryKey: ['vendor-bills', tenantId, 'stats'], queryFn: () => vendorBillsApi.stats(), staleTime: 15_000 });
}

export function useVendorBill(id: string | undefined): UseQueryResult<VendorBill | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['vendor-bills', tenantId, 'detail', id],
    queryFn: () => (id ? vendorBillsApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

/** Bills touch PO detail (billed qty, bills card) and supplier outstanding — invalidate those too. */
function useInvalidate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['vendor-bills', tenantId] });
    void qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
    void qc.invalidateQueries({ queryKey: ['suppliers', tenantId] });
  };
}

export function useCreateVendorBill() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (payload: CreateVendorBillPayload) => vendorBillsApi.create(payload), onSuccess: () => invalidate() });
}

export function useUpdateVendorBill() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVendorBillPayload }) => vendorBillsApi.update(id, payload),
    onSuccess: () => invalidate(),
  });
}

export function usePostVendorBill() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, confirmDuplicate }: { id: string; confirmDuplicate?: boolean }) => vendorBillsApi.post(id, confirmDuplicate),
    onSuccess: () => invalidate(),
  });
}

export function useRecordVendorPayment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RecordVendorPaymentPayload }) => vendorBillsApi.recordPayment(id, payload),
    onSuccess: () => invalidate(),
  });
}

export function useVoidVendorPayment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, paymentId, reason }: { id: string; paymentId: string; reason: string }) =>
      vendorBillsApi.voidPayment(id, paymentId, reason),
    onSuccess: () => invalidate(),
  });
}

export function useCancelVendorBill() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => vendorBillsApi.cancel(id, reason),
    onSuccess: () => invalidate(),
  });
}

/** Fetches the bill PDF blob; read-only, no invalidation. */
export function useDownloadVendorBillPdf() {
  return useMutation({ mutationFn: (id: string) => vendorBillsApi.downloadPdf(id) });
}

export function useAddVendorBillDocument() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PurchaseDocumentPayload }) => vendorBillsApi.addDocument(id, payload),
    onSuccess: () => invalidate(),
  });
}

export function useAddPoDocument() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ poId, payload }: { poId: string; payload: PurchaseDocumentPayload }) => vendorBillsApi.addPoDocument(poId, payload),
    onSuccess: () => invalidate(),
  });
}

export function useDeletePoDocument() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ poId, docId }: { poId: string; docId: string }) => vendorBillsApi.deletePoDocument(poId, docId),
    onSuccess: () => invalidate(),
  });
}
